/* eslint-disable consistent-return */

/**
 * @file Loan 质押借贷通用数据模块
 * @date 2022-8-05
 */
import { loanApi } from '@/apis/spot';
import { times } from '@/utils/number';
import { formatPrecision } from '@/utils/tools';
import { getUserInfo } from '@/utils/userInfo';
import { accounts } from '@/config/loan';

const state = {
    user: {}, // 用户信息
    tab: 'Loan', // 当前tab
    loanToken: '', // 借贷币种
    pledgeToken: '', // 质押币种
    progressOrderQty: -1, // 进行中的订单数量
    allTokens: [], // 借贷&质押所有币种
    loanTokens: [], // 借贷币种列表
    pledgeTokens: [], // 质押币种列表
    marketMap: {}, // 行情币对map
    isOpenVideo: false, // 是否打开视频教程
    videoInfo: null, // 视频信息
    account: null, // 其他账户【合约、反向合约、USDC合约】
    accountLoanTokens: [], // 其他账户的借贷币种列表
    isSetLoanToken: false, // 是否设置初始借贷币种
};

const getters = {
    // 借贷币种map
    loanTokensMap(state) {
        return state.loanTokens.reduce((data, item) => {
            data[item.token] = {
                ...item,
            };
            return data;
        }, {});
    },
    // 其他账户借贷币种map
    accountLoanTokensMap(state) {
        return state.accountLoanTokens.reduce((data, item) => {
            data[item.token] = {
                ...item,
            };
            return data;
        }, {});
    },
    // 质押币种map
    pledgeTokensMap(state) {
        return state.pledgeTokens.reduce((data, item) => {
            data[item.token] = {
                ...item,
            };
            return data;
        }, {});
    },
    // 币种行情map { USDT: '1', BTC: '146.99'} [币种USDT的价格]
    tokensMap(state) {
        return state.allTokens?.reduce((data, item) => {
            data[item] = {
                ...data[item],
                c: item === 'USDT' ? '1' : state.marketMap[`${item}USDT`]?.c,
            };
            return data;
        }, {});
    },
    // 是否为其他有效账户【合约、反向合约、USDC合约】
    isEnabledAccount(state) {
        return state.account && state.account !== 'spot' && accounts[state.account];
    },
};

const mutations = {
    // 设置账户
    SET_ACCOUNT(state, payload) {
        state.account = payload;
    },
    // 设置借贷币种
    SET_IS_LOAN_TOKEN(state, payload) {
        state.isSetLoanToken = payload;
    },
    // 设置用户信息
    SET_USER_INFO(state, payload) {
        state.user = payload;
    },
    // 设置借贷币种
    SET_LOAN_TOKEN(state, payload) {
        state.loanToken = payload;
    },
    // 设置账户借贷币种列表
    SET_ACCOUNT_LOAN_TOKEN(state, payload) {
        state.accountLoanTokens = payload;
    },
    // 设置质押币种
    SET_PLEDGE_TOKEN(state, payload) {
        state.pledgeToken = payload;
    },
    // 设置当前tab
    SET_TAB(state, payload) {
        state.tab = payload;
    },
    // 设置借贷币种列表
    SET_LOAN_TOKENS(state, payload) {
        state.loanTokens = payload.map((item) => ({
            ...item,
            dayInterestRate: times(item.hourlyInterestRate, 24),
            interestRatePrecision: formatPrecision(item.hourlyInterestRate),
        }));
    },
    // 设置质押币种列表
    SET_PLEDGE_TOKENS(state, payload) {
        state.pledgeTokens = payload;
    },
    // 设置进行中订单数量
    SET_PROGRESS_ORDER_QTY(state, payload) {
        state.progressOrderQty = payload;
    },
    // 聚合所有币种(包含借贷&质押)
    SET_ALL_TOKENS(state, payload) {
        const arr = payload.map((item) => item.token);
        state.allTokens = [...new Set([...state.allTokens, ...arr])];
    },
    // 设置行情数据
    SET_MARKETLIST(state, payload) {
        payload.forEach((item) => {
            state.marketMap[item.s] = item;
        });
    },
    // 设置视频教程开启
    SET_OPEN_VIDEO(state, payload) {
        state.isOpenVideo = payload;
    },
    // 设置视频教程信息
    SET_VIDEO_INFO(state, payload) {
        state.videoInfo = payload;
    },
};

const actions = {
    // 获取用户信息
    async GET_USER_INFO({ commit }) {
        const res = await loanApi.getUserInfo();
        if (res && res.success && res.result) {
            const { result } = res;
            commit('SET_USER_INFO', result);
            commit('SET_PROGRESS_ORDER_QTY', result.unclearOrderCount);
        }
    },
    // 获取现货所有借贷币种列表
    async GET_LOAN_TOKENS({ commit }) {
        const res = await loanApi.getLoanTokens();
        if (res.success && res.result && res.result.length) {
            const { result } = res;
            commit('SET_LOAN_TOKENS', result);
            commit('SET_ALL_TOKENS', result);
        }
    },
    // 获取其他账户支持的借贷币种列表
    async GET_ACCOUNT_LOAN_TOKENS({ commit, state }) {
        const res = await loanApi.getLoanTokens({
            accountType: accounts[state.account]?.type,
        });
        if (res.success && res.result && res.result.length) {
            const { result } = res;
            const enabledResult = result.filter((item) => item.enabled === 'Y');
            // 列表为空则重置为现货账户
            if (!enabledResult.length) {
                commit('SET_ACCOUNT', 'spot');
                commit('SET_IS_LOAN_TOKEN', true);
            }
            commit('SET_ACCOUNT_LOAN_TOKEN', enabledResult);
        } else {
            commit('SET_ACCOUNT', 'spot');
            commit('SET_IS_LOAN_TOKEN', true);
        }
    },
    // 获取质押币种列表
    async GET_PLEDGE_TOKENS({ commit }) {
        const res = await loanApi.getPledgeTokens();
        if (res.success && res.result && res.result.length) {
            const { result } = res;
            commit('SET_PLEDGE_TOKENS', result);
            commit('SET_ALL_TOKENS', result);
        }
    },
    // 初始化
    async INIT({ dispatch }) {
        const userInfo = await getUserInfo();
        if (userInfo) {
            // 用户信息
            dispatch('GET_USER_INFO');
            // 资产币种，拿取币种精度
            dispatch('settings/GET_ASSET_CURRENCY', null, { root: true });
        }
        // 借贷币种
        dispatch('GET_LOAN_TOKENS');
        // 质押币种
        dispatch('GET_PLEDGE_TOKENS');
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
